import { QIcon_close_default, QIcon_search_default } from '@repo/icons/index'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { STACK_ACTIVITY_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { n_upcoming_lessons, s_blog } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { SelectMaterialContext } from '../../../../pages/[lang]/portal/lessons/materials/[lessonId]'
import { AI_TUTOR_ACTIVITIES_MAP, useAITutorStack } from '../../../../pages/[lang]/webview/ai-tutor'
import { useConversationAIStack } from '../../../../pages/[lang]/webview/conversational-ai'
import { useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../../pages/[lang]/webview/materials'

const highlightText = (text, highlight) => {
  // 정규표현식으로 검색어 찾기
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? <strong key={i}>{part}</strong> : part
      )}
    </span>
  )
}

const SearchTab = ({ isMobile, value, onChange, handleClose, handleKeyDown }) => {
  const { t } = useTranslation()
  const focusRef = useRef(null)

  return (
    <div className={style.searchTabLayout(isMobile)}>
      <div className="ml-0 mr-[8px] self-center">
        <QIcon_search_default color="#80839E" width={24} height={24} />
      </div>

      <Textarea
        ref={focusRef}
        value={value}
        onChange={onChange}
        rows={1}
        placeholder={t(n_upcoming_lessons.edit_materials.search_placeholder)}
        draggable={false}
        className={classNames(
          ' Body2-14Md w-full flex-grow cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none',
          !isMobile && 'Body1-16Md',
          isMobile && 'Body2-14Md'
        )}
        onKeyDown={(e) => handleKeyDown(e, value)}
      ></Textarea>

      {value && (
        <div className="cursor-pointer self-center rounded-full bg-gray-200 p-[2px]" onClick={handleClose}>
          <QIcon_close_default color="#FFFFFF" width={16} height={16} />
        </div>
      )}
    </div>
  )
}

interface IBlogSearchTab {
  isMobile?: boolean
  isList?: boolean
  webviewKeyword?: string
  setWebviewKeyword?: any
  lessonId?: any
  isConversation?: boolean
  isAITutor?: boolean
  className?: string
}
const SelectMaterialSearchSection = ({
  isMobile = false,
  isList = false,
  webviewKeyword,
  setWebviewKeyword,
  lessonId = '',
  isConversation = false,
  isAITutor = false,
  className = '',
}: IBlogSearchTab) => {
  const router = useAppRouter()
  const isWebview = useIsWebview()
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [result, setResult] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const layoutRef = useRef(null)
  const { setKeywords } = useContext(SelectMaterialContext)

  const { useLessonsFlow } = useLessonsStack()
  const { push: lessonPush } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: lessonPush })

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialsPush } = useMaterialsFlow()
  const materialsRouter = useAppRouter({ stackPush: materialsPush })

  const { useConversationAIFlow } = useConversationAIStack()
  const { push: conversationPush } = useConversationAIFlow()
  const conversationRouter = useAppRouter({ stackPush: conversationPush })

  const { useAITutorFlow } = useAITutorStack()
  const { push: aiTutorPush, pop } = useAITutorFlow()
  const aiTutorRouter = useAppRouter({ stackPush: aiTutorPush, browserDetect: true })

  const lessonSelectLessonId = lessonId ? lessonId : router.query.lessonId

  const fetchMaterialSearchData = async (value: string) => {
    if (value.length < 1 || !value) return

    setLoading(true)
    const response = await axiosGet(apis.course.search(), {
      param: { keyword: value },
    })

    if (response.success) {
      setResult(response.courses)
    }

    setLoading(false)
  }

  const debouncedFetch = useCallback(
    debounce((value) => fetchMaterialSearchData(value), 300),
    []
  )

  const debouncedFetchWebview = useCallback(
    debounce((webviewKeyword) => fetchMaterialSearchData(webviewKeyword), 300),
    []
  )
  const handleSetValue = (value) => {
    setValue(value)
    debouncedFetch(value)
  }

  const handleSetValueWebview = (value) => {
    setWebviewKeyword && setWebviewKeyword(value)
    debouncedFetchWebview(value)
  }
  const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    handleSetValue(e.target.value)
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setValue('')
    setKeywords('')
    setWebviewKeyword && setWebviewKeyword('') // webview
  }

  const handleKeyDown = (event, keywords) => {
    console.log(event)
    console.log(keywords)

    if (event.key === 'Enter') {
      event.preventDefault()
      setResult([])
      setOpen(false)
      setKeywords(keywords)
      setWebviewKeyword && setWebviewKeyword(keywords) // webview
    }
  }

  const onClickCourseDetail = (item) => {
    if (isConversation) {
      conversationRouter.push('', {
        stackActivity: STACK_ACTIVITY_NAME.CONVERSATION_MATERIAL_DETAIL_ACTIVITY,
        stackSendData: {
          materialId: item.id,
        },
      })
      return
    }

    if (isAITutor) {
      aiTutorPush(AI_TUTOR_ACTIVITIES_MAP.AITutorDiscussionMaterialDetailActivity, { materialId: item.id })
      return
    }

    router.asPath.includes('webview/materials')
      ? materialsRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(item.id)
            : urls.student.newPortal.materials.detail(item.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
            stackSendData: {
              materialId: item.id,
              lessonId: lessonSelectLessonId,
            },
          }
        )
      : lessonRouter.push(
          isList
            ? urls.student.newPortal.materials.detail(item.id)
            : urls.student.newPortal.materials.detail(item.id) + `?lessonId=${lessonSelectLessonId}`,
          {
            stackActivity: STACK_ACTIVITY_NAME.LESSON_MATERIAL_DETAIL_ACTIVITY,
            stackSendData: {
              materialId: item.id,
              lessonId: lessonSelectLessonId,
            },
          }
        )
  }

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!(layoutRef?.current === event.target || layoutRef?.current?.contains(event.target))) {
        setOpen(false)
        setValue('')
        setKeywords('')
        setWebviewKeyword && setWebviewKeyword('') // webview
      }
    }

    if (open) {
      document.addEventListener('click', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [open])

  return (
    <div
      className={classNames(
        isList ? 'max-w-[336px]' : !isWebview ? 'max-w-[56%]' : 'px-[16px]',
        'relative z-[20] h-[50px] w-full',
        className
      )}
      ref={layoutRef}
    >
      <SearchTab
        isMobile={isMobile}
        value={value}
        onChange={onChange}
        handleClose={handleClose}
        handleKeyDown={handleKeyDown}
      />

      {open && value.length > 1 && (
        <>
          {isWebview ? (
            <div className="flex flex-col items-start text-start w-full bg-white">
              {loading ? (
                <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                  {t(s_blog.search_bar.placeholder_loading)}
                </div>
              ) : result.length > 0 ? (
                result.map((item, i) => {
                  return (
                    <button
                      key={i}
                      className="flex w-full justify-start rounded-[4px] hover:bg-gray-50"
                      onClick={() => onClickCourseDetail(item)}
                    >
                      <div className={style.searchResultElement}>{highlightText(item.title, value)}</div>
                    </button>
                  )
                })
              ) : (
                <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                  {t(s_blog.search_bar.placeholder_no_result)}
                </div>
              )}
            </div>
          ) : (
            <div className={style.searchResultLayout}>
              <div className="flex flex-col items-start text-start">
                {loading ? (
                  <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                    {t(s_blog.search_bar.placeholder_loading)}
                  </div>
                ) : result.length > 0 ? (
                  result.map((item, i) => {
                    return (
                      <button
                        key={i}
                        className="flex w-full justify-start rounded-[4px] hover:bg-gray-50"
                        onClick={() =>
                          router.push(
                            isList
                              ? urls.student.newPortal.materials.detail(item.id)
                              : urls.student.newPortal.materials.detail(item.id) + `?lessonId=${lessonSelectLessonId}`
                          )
                        }
                      >
                        <div className={style.searchResultElement}>{highlightText(item.title, value)}</div>
                      </button>
                    )
                  })
                ) : (
                  <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                    {t(s_blog.search_bar.placeholder_no_result)}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <br />
    </div>
  )
}

export default SelectMaterialSearchSection

const Textarea = (props) => (
  <textarea
    className="Body2-14Md w-full cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none"
    {...props}
  ></textarea>
)

const style = {
  searchTabLayout: (isMobile) => {
    return isMobile
      ? `border-1 flex w-full max-w-[688px] items-center rounded-[8px] border-gray-200 bg-gray-50 p-[12px] focus-within:border-purple-500`
      : `border-1 flex max-w-[688px] items-center rounded-[8px] border-gray-200 bg-gray-50 p-[12px] focus-within:border-purple-500`
  },
  searchResultLayout: `border-1 absolute z-[20] mt-[4px] w-full max-w-[688px] rounded-[8px] bg-white p-[8px] shadow-[0_2px_8px_0_rgba(0,0,0,0.15)]`,
  searchResultElement: `Body1-16Md rounded-[4px] px-[16px] py-[8px] hover:bg-gray-50`,
  searchResultMoWebElement: ` rounded-[4px] p-[8px]`,
}
